import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzMessageService } from 'ng-zorro-antd/message';

import { CustomerCustomFieldsService } from './services/customer-custom-fields.service';
import { CustomerCustomField } from './models/customer-360.types';

interface EditableField extends CustomerCustomField {
  /** Client-only flag — true for rows inserted since the last save. */
  _new?: boolean;
}

/**
 * Customer custom fields editor.
 *
 * Moved out of the all-in-one Customer 360 content panel into its own
 * tab so the user can focus on preferences (hair formula, allergies,
 * drink of choice, …) without scrolling past analytics.
 *
 * Hits the dedicated `GET /users/:id/custom-fields` endpoint on first
 * open and `PUT .../bulk` on save — the bulk endpoint is transactional,
 * so a save either fully replaces the customer's org-wide field set
 * or leaves it untouched.
 *
 * UI modernization vs. the original inline editor:
 *  - Quick-insert chips for the common keys (hair formula / allergies /
 *    drink / notes) so staff don't have to retype Georgian field names.
 *  - Dirty banner with explicit save/cancel, instead of the old
 *    always-visible dashed-button layout.
 *  - Each row rendered as a rounded "chip-card" with inline delete.
 */
@Component({
  selector: 'beautyapp-customer-360-custom-fields',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzEmptyModule,
  ],
  template: `
    <nz-spin [nzSpinning]="loading()" nzSize="large">
      <section class="cf">
        <header class="cf-head">
          <div>
            <h3>დამატებითი ინფორმაცია</h3>
            <span class="sub">მომხმარებლის პიროვნული დეტალები, შენახული ორგანიზაციის დონეზე</span>
          </div>
          <button nz-button nzType="primary" (click)="addCf()">
            <span nz-icon nzType="plus"></span> ახალი ველი
          </button>
        </header>

        <div class="chips">
          <span class="chips-label">სწრაფი დამატება:</span>
          <button
            *ngFor="let preset of presets"
            class="chip"
            type="button"
            (click)="addPreset(preset)"
          >
            <span nz-icon [nzType]="preset.icon"></span>
            {{ preset.label }}
          </button>
        </div>

        <div class="list" *ngIf="fields().length; else emptyTpl">
          <div class="row" *ngFor="let f of fields(); let i = index; trackBy: trackCf">
            <div class="key">
              <input
                nz-input
                placeholder="ველის სახელი"
                [(ngModel)]="f.fieldKey"
                (ngModelChange)="markDirty()"
              />
            </div>
            <div class="val">
              <input
                nz-input
                placeholder="მნიშვნელობა"
                [ngModel]="f.fieldValue"
                (ngModelChange)="setValue(i, $event)"
              />
            </div>
            <button
              nz-button
              nzType="text"
              nzDanger
              class="row-del"
              (click)="remove(i)"
              nz-tooltip="წაშლა"
            >
              <span nz-icon nzType="delete"></span>
            </button>
          </div>
        </div>
        <ng-template #emptyTpl>
          <div class="empty" *ngIf="!loading()">
            <nz-empty nzNotFoundImage="simple"></nz-empty>
            <div class="empty-hint">
              ჯერ არცერთი ველი არ არის დამატებული — დაიწყე სწრაფი ჩიპით ან „ახალი ველი"-თ.
            </div>
          </div>
        </ng-template>

        <div class="save-bar" *ngIf="dirty()">
          <span class="dirty-dot"></span>
          ცვლილებები არ არის შენახული
          <div class="spacer"></div>
          <button nz-button (click)="cancel()">გაუქმება</button>
          <button nz-button nzType="primary" [nzLoading]="saving()" (click)="save()">
            <span nz-icon nzType="save"></span> შენახვა
          </button>
        </div>
      </section>
    </nz-spin>
  `,
  styles: [`
    :host { display: block; }
    .cf { display: flex; flex-direction: column; gap: 16px; padding: 4px 4px 24px; }
    .cf-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
    .cf-head h3 { margin: 0; font-size: 16px; font-weight: 650; color: #101828; }
    .cf-head .sub { color: #98a2b3; font-size: 12px; }

    .chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
      padding: 10px 12px;
      background: #f8fafc;
      border: 1px dashed #e4e7ec;
      border-radius: 12px;
    }
    .chips-label { font-size: 12px; color: #667085; margin-right: 4px; }
    .chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: #fff;
      border: 1px solid #e4e7ec;
      border-radius: 999px;
      font-size: 12px;
      color: #344054;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .chip:hover { border-color: #6366f1; color: #6366f1; transform: translateY(-1px); }
    .chip span[nz-icon] { font-size: 12px; }

    .list { display: flex; flex-direction: column; gap: 10px; }
    .row {
      display: grid;
      grid-template-columns: minmax(160px, 1fr) minmax(200px, 2fr) auto;
      gap: 10px;
      align-items: center;
      padding: 12px;
      background: #fff;
      border: 1px solid #eef0f3;
      border-radius: 12px;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .row:hover { border-color: #d0d5dd; box-shadow: 0 2px 6px rgba(16,24,40,0.04); }
    .row-del { align-self: center; }

    .empty { display: flex; flex-direction: column; align-items: center; padding: 32px 16px; }
    .empty-hint { color: #667085; font-size: 13px; margin-top: 8px; text-align: center; }

    .save-bar {
      position: sticky;
      bottom: 0;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      margin-top: 4px;
      background: #fffbeb;
      border: 1px solid #fde68a;
      border-radius: 12px;
      color: #92400e;
      font-size: 13px;
      box-shadow: 0 -4px 12px rgba(16,24,40,0.05);
    }
    .save-bar .spacer { flex: 1; }
    .dirty-dot {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #f59e0b;
      box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
    }
  `],
})
export class Customer360CustomFieldsComponent {
  userId = input.required<string>();

  private api = inject(CustomerCustomFieldsService);
  private msg = inject(NzMessageService);

  loading = signal(true);
  saving = signal(false);
  fields = signal<EditableField[]>([]);
  private original = signal<EditableField[]>([]);
  dirty = computed(() => {
    const a = this.fields();
    const b = this.original();
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i++) {
      if (a[i].fieldKey !== b[i].fieldKey || a[i].fieldValue !== b[i].fieldValue) return true;
    }
    return false;
  });

  presets: { key: string; label: string; icon: string }[] = [
    { key: 'თმის ფორმულა',  label: 'თმის ფორმულა',  icon: 'experiment' },
    { key: 'ალერგიები',     label: 'ალერგიები',     icon: 'warning' },
    { key: 'სასმელი',       label: 'სასმელი',       icon: 'coffee' },
    { key: 'შენიშვნა',      label: 'შენიშვნა',      icon: 'file-text' },
  ];

  constructor() {
    effect(() => {
      const id = this.userId();
      if (id) this.load(id);
    });
  }

  private load(id: string): void {
    this.loading.set(true);
    this.api.list(id).subscribe({
      next: (rows) => {
        const cloned = (rows ?? []).map((r) => ({ ...r }));
        this.fields.set(cloned);
        this.original.set(cloned.map((r) => ({ ...r })));
        this.loading.set(false);
      },
      error: () => {
        this.fields.set([]);
        this.original.set([]);
        this.loading.set(false);
        this.msg.error('ველების ჩატვირთვა ვერ მოხერხდა');
      },
    });
  }

  trackCf(_: number, f: EditableField) { return f.id || f.fieldKey; }
  markDirty() { /* computed handles detection; here for ngModelChange hook */ }

  setValue(i: number, value: string) {
    const arr = [...this.fields()];
    arr[i] = { ...arr[i], fieldValue: value };
    this.fields.set(arr);
  }

  addCf(key = '', value: string | null = '') {
    this.fields.set([
      ...this.fields(),
      { id: 0, fieldKey: key, fieldValue: value, _new: true },
    ]);
  }

  addPreset(p: { key: string; label: string }) {
    // Avoid adding an already-present preset twice — jump focus instead.
    const existing = this.fields().findIndex((f) => f.fieldKey === p.key);
    if (existing !== -1) {
      this.msg.info('ეს ველი უკვე არსებობს');
      return;
    }
    this.addCf(p.key, '');
  }

  remove(i: number) {
    const arr = [...this.fields()];
    arr.splice(i, 1);
    this.fields.set(arr);
  }

  cancel() {
    this.fields.set(this.original().map((r) => ({ ...r })));
  }

  save(): void {
    const payload = this.fields()
      .filter((f) => f.fieldKey.trim())
      .map((f) => ({ fieldKey: f.fieldKey.trim(), fieldValue: f.fieldValue }));
    this.saving.set(true);
    this.api.bulkReplace(this.userId(), payload).subscribe({
      next: (rows) => {
        const cloned = rows.map((r) => ({ ...r }));
        this.fields.set(cloned);
        this.original.set(cloned.map((r) => ({ ...r })));
        this.saving.set(false);
        this.msg.success('შენახულია');
      },
      error: () => {
        this.saving.set(false);
        this.msg.error('შენახვა ვერ მოხერხდა');
      },
    });
  }
}
